/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React from 'react';
import { useFeatureAppTracking } from '@oneaudi/feature-app-utils';
import type { ApolloClient } from '@oneaudi/onegraph-client';
import { OneGraphProvider } from '@oneaudi/onegraph-client';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { getThemeName } from '../alpha-helper';
import type { TrackingService } from './content/feature-service-types';
import { EnhancedFloatingButtons } from './ui/components/enhanced-web-chat-widget/floating-buttons/EnhancedFloatingButtons';
import { EnhancedWebChatProvider } from './ui/context/chat-widget-context/chatWidgetContext';

interface FeatureAppProps {
  oneGraphClient: ApolloClient<unknown>;
  trackingService?: TrackingService;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  oneGraphClient,
  trackingService,
}: FeatureAppProps) => {
  const ref = React.useRef(null);
  const { track } = useFeatureAppTracking({
    trackingService,
    ref,
    attributes: {
      implementer: 37,
      version: __FEATURE_APP_VERSION__,
    },
  });
  React.useEffect(() => {
    if (trackingService) {
      track.sendReadyEvent();
      track.registerImpressionTracking();
    }
  }, [track, trackingService]);

  return (
    <ThemeProvider theme={{ name: getThemeName() }}>
      <main ref={ref} data-testid="fa-omnichannel-chat">
        <OneGraphProvider client={oneGraphClient}>
          <EnhancedWebChatProvider>
            <EnhancedFloatingButtons />
          </EnhancedWebChatProvider>
        </OneGraphProvider>
      </main>
    </ThemeProvider>
  );
};

export default FeatureApp;
